import { ActionsHeaderCell } from '@/components/ActionsHeaderCell';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { importProfilesApi, type ImportProfile } from '@/api/importProfiles';
import { errorCopy } from '@/api/errorCopy';
import { AiPill } from '@/components/AiPill';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { KindChip } from '@/components/KindChip';
import { ErrorState } from '@/components/ErrorState';
import { TableCard } from '@/components/TableCard';
import { useConfirm } from '@/components/useConfirm';
import { PageHeader } from '@/shell/PageHeader';
import { usePageTitle } from '@/shell/usePageTitle';

export function ProfilesPage() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<ImportProfile[] | null>(null);
  const [loadFailure, setLoadFailure] = useState<{ error: unknown } | null>(null);
  const [names, setNames] = useState<Record<number, string>>({});
  const deleting = useConfirm<ImportProfile>();
  const started = useRef(false);

  const title = usePageTitle({ title: t('imports.profilesTitle') });
  const header = (
    <PageHeader title={title} back={{ label: t('nav.accounts'), to: '/me/accounts' }} />
  );

  const load = useCallback(async () => {
    try {
      const list = await importProfilesApi.list();
      setRows(list);
      setNames(Object.fromEntries(list.map((p) => [p.id, p.name])));
      setLoadFailure(null);
    } catch (error) {
      setLoadFailure({ error });
    }
  }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void load();
  }, [load]);

  if (loadFailure !== null) {
    return (
      <>
        {header}
        <ErrorState
          error={loadFailure.error}
          what={t('errors.what.importProfiles')}
          onRetry={() => void load()}
        />
      </>
    );
  }

  if (rows === null) {
    return (
      <>
        {header}
        <Typography>{t('common.loading')}</Typography>
      </>
    );
  }

  return (
    <>
      {header}
      <Stack spacing={2}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('imports.profilesHelp')}
        </Typography>
        <TableCard name={t('imports.profilesTitle')} minWidth={880}>
          <Table size="small" aria-label={t('imports.profilesTitle')}>
            <TableHead>
              <TableRow>
                <TableCell>{t('imports.profileName')}</TableCell>
                <TableCell>{t('imports.profileScope')}</TableCell>
                <TableCell>{t('imports.colOrigin')}</TableCell>
                <TableCell>{t('imports.dateFormat')}</TableCell>
                <TableCell>{t('imports.amountMode')}</TableCell>
                <TableCell>{t('imports.filenamePattern')}</TableCell>
                <ActionsHeaderCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <TextField
                      size="small"
                      value={names[row.id] ?? row.name}
                      slotProps={{ htmlInput: { 'aria-label': t('imports.rename') } }}
                      onChange={(event) =>
                        setNames((prev) => ({ ...prev, [row.id]: event.target.value }))
                      }
                      onBlur={() => {
                        const name = (names[row.id] ?? row.name).trim();
                        if (name.length === 0 || name === row.name) return;
                        void importProfilesApi.update(row.id, { name }).then(() => void load());
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <FormControl size="small">
                      <InputLabel id={`scope-${row.id}`}>{t('imports.profileScope')}</InputLabel>
                      <Select
                        labelId={`scope-${row.id}`}
                        label={t('imports.profileScope')}
                        value={row.scope}
                        onChange={(event) => {
                          const scope = event.target.value as 'ACCOUNT' | 'OWNER';
                          if (scope === 'OWNER') {
                            void importProfilesApi
                              .update(row.id, { accountId: null })
                              .then(() => void load());
                            return;
                          }
                          if (row.accountId !== null) {
                            void importProfilesApi
                              .update(row.id, { accountId: row.accountId })
                              .then(() => void load());
                          }
                        }}
                      >
                        <MenuItem value="ACCOUNT" disabled={row.accountId === null}>
                          {t('imports.scopeAccount')}
                        </MenuItem>
                        <MenuItem value="OWNER">{t('imports.scopeOwner')}</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    {row.origin === 'AI' ? (
                      <AiPill />
                    ) : (
                      <KindChip neutral label={t('imports.origin.USER')} />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="mono">{row.dateFormat}</Typography>
                  </TableCell>
                  <TableCell>
                    {t(`imports.amount${row.amountMode === 'SIGNED' ? 'Signed' : 'DebitCredit'}`)}
                  </TableCell>
                  <TableCell>{row.filenamePattern ?? '—'}</TableCell>
                  <TableCell>
                    <Button size="small" color="error" onClick={() => deleting.ask(row)}>
                      {t('imports.deleteProfile')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableCard>
        {deleting.target !== null && (
          <ConfirmDialog
            title={t('imports.deleteProfileTitle', { name: deleting.target.name })}
            consequence={t('imports.deleteProfileConsequence')}
            confirmLabel={t('imports.deleteProfileConfirm')}
            destructive
            busy={deleting.busy}
            error={deleting.error}
            onConfirm={() =>
              void deleting.run(
                async (row) => {
                  await importProfilesApi.remove(row.id);
                  await load();
                },
                (error) => errorCopy(t, error, 'accounts.errorGeneric'),
              )
            }
            onCancel={deleting.cancel}
          />
        )}
      </Stack>
    </>
  );
}
